import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { IUpdateState, restoreState, updateState } from '../../../actions/product/state';
import { EntityTypes } from 'constants/entities';
import { ActionType, IAction, IActionBasic, IActionDAO } from 'types/domain/action';
import { assetId, AssetType, IAsset } from 'types/app/asset';
import { selectScene } from 'core/selectors/product/scene/scene';
import { ISceneDAO, SceneType } from 'types/domain/scene';
import { IKeyArray } from 'types/IKeyArray';
import { setAssets } from 'core/slices/assets';
import { selectActions } from 'core/selectors/product/action/actions';
import { IImageDAO, IImageFile, ImageFileType, Resolutions } from 'types/domain/image';
import { IVideoDAO, IVideoFile, VideoType } from 'types/domain/video';
import { isSafari } from 'react-device-detect';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { createRequest, getRequest } from 'core/services/admin';
import { ICreate, IGet } from 'core/actions/admin/data';
import { RootState } from 'types';
import { selectAction } from 'core/selectors/product/action/action';
import { IModelDAO } from 'types/domain/model';
import { LoadingState } from 'types/domain/base';
import { moduleRequest } from 'core/services/product';
import { IModuleAction } from 'types/action/player';
import { IPreview, preview } from 'core/slices/register';
import { selectVerificationRequest } from 'core/selectors/product/verificationRequest';
import { IVerificationRequestDAO } from 'types/domain/verification';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { ICourseNodeDAO } from 'types/domain/course';
import { queueItem } from 'core/slices/queue';
import { assetLog, assetWarn } from 'utils/assetLog';

export interface ILoader {
  [assetId: string]: any;
}

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  yield parseAssets(action.payload.type, action.payload.id);
}

function* getRequestFulfilled(action) {
  const { type, entityId } = action.meta.arg as IGet;
  yield parseAssets(type, entityId);
}

function* moduleRequestFulfilled(action) {
  const { id } = action.meta.arg as IModuleAction;
  yield parseAssets(EntityTypes.MODULE, id);
}

function* restoreStateSaga(action: PayloadAction<IUpdateState[]>) {
  yield all(action.payload.map(update => parseAssets(update.type, update.id)));
}

function* previewSaga(action: PayloadAction<IPreview>) {
  if (action.payload.moduleId) yield parseAssets(EntityTypes.MODULE, action.payload.moduleId);
}

function* queueSaga(action: PayloadAction<IPreview>) {
  if (action.payload.courseNodeId) yield parseAssets(EntityTypes.COURSE_NODE, action.payload.courseNodeId);
}

function* createRequestFulfilled(action) {
  const { type } = action.meta.arg as ICreate;
  const data: any = action.payload;
  yield parseAssets(type, data.id);
}

export function* parseAssets(type: EntityTypes, id?: number) {
  if (!id) return;

  switch (type) {
    case EntityTypes.MODULE:
      const actions: IKeyArray<IActionDAO> = yield select(selectActions);

      const scenes: ISceneDAO[] = actions
        ? yield all(
            Object.values(actions)
              .filter(action => !!action && !!action.scene)
              .filter(
                action =>
                  [SceneType.VIDEO, SceneType.MODEL].includes(action.scene?.type) ||
                  action.type === ActionType.INTERACTIVE,
              )
              .reduce((prev, action: IAction) => {
                if (action.sceneId)
                  //@ts-ignore
                  prev = [...prev, select(state => selectScene(state, action.sceneId))];
                //@ts-ignore if(action.voiceSceneId)
                prev = [...prev, select(state => selectScene(state, action.voiceSceneId))];

                return prev;
              }, []),
          )
        : [];

      const sceneAssets: IKeyArray<IAsset> = scenes.reduce((prev, scene: ISceneDAO) => {
        mergeEntities(prev, EntityTypes.SCENE, scene);
        return prev;
      }, {});

      // const entities: IActionEntityDAO[] = actions
      //   ? Object.values(actions).reduce((prev, action: IActionDAO) => [...prev, ...action.entities], [])
      //   : [];

      // const entityAssets: IKeyArray<IAsset> = entities.reduce((prev, ent) => {
      //   mergeEntities(prev, EntityTypes.ACTION_ENTITY, ent);
      //   return prev;
      // }, {});

      yield put(yield call(setAssets, sceneAssets));

      break;
    case EntityTypes.SCENE:
      //@ts-ignore
      const scene: ISceneDAO = yield select(state => selectScene(state, id));
      //@ts-ignore
      const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, id));

      if (!sceneAsset && scene) {
        const assets = {};
        mergeEntities(assets, EntityTypes.SCENE, scene);
        if (assets) yield put(yield call(setAssets, assets));
      }
      break;
    case EntityTypes.ACTION:
      //@ts-ignore
      const action: IActionBasic = yield select((s: RootState) => selectAction(s, id));
      if (!action || !action.sceneId) return;
      //@ts-ignore
      const actionScene: ISceneDAO = yield select((s: RootState) => selectScene(s, action.sceneId));
      //@ts-ignore
      const actionSceneAsset: IAsset = yield select((s: RootState) => selectSceneAsset(s, action.sceneId));
      if (!actionSceneAsset && actionScene) {
        const assets = {};
        mergeEntities(assets, EntityTypes.SCENE, actionScene);
        if (assets) yield put(yield call(setAssets, assets));
      }
      //@ts-ignore
      const actionVoiceScene: ISceneDAO = yield select((s: RootState) => selectScene(s, action.voiceSceneId));
      //@ts-ignore
      const actionVoiceSceneAsset: IAsset = yield select((s: RootState) => selectSceneAsset(s, action.voiceSceneId));
      if (!actionVoiceSceneAsset && actionVoiceScene) {
        const assets = {};
        mergeEntities(assets, EntityTypes.SCENE, actionVoiceScene);
        if (assets) yield put(yield call(setAssets, assets));
      }

      break;
    case EntityTypes.VERIFICATION_REQUEST:
      const verificationRequest: IVerificationRequestDAO = yield select((state: RootState) =>
        selectVerificationRequest(state, id),
      );

      if (verificationRequest.image) {
        const assets = {};
        mergeEntities(assets, EntityTypes.VERIFICATION_REQUEST, verificationRequest);
        yield put(yield call(setAssets, assets));
      }

      break;
    case EntityTypes.COURSE_NODE:
      const courseNode: ICourseNodeDAO = yield select((state: RootState) => selectCourseNode(state, id));

      if (courseNode?.image) {
        const assets = {};
        mergeEntities(assets, EntityTypes.COURSE_NODE, courseNode);
        yield put(yield call(setAssets, assets));
      }

      break;
  }
}

function mergeEntities(prev, entityType: EntityTypes, entity: any) {
  if (!entity) return prev;

  const sceneId = entityType === EntityTypes.SCENE ? entity.id : null;

  if (entity.image && !prev[assetId(entityType, entity, AssetType.IMAGE, entity.image.id)])
    mergeAsset(prev, entityType, AssetType.IMAGE, entity, entity.image.id, sceneId);
  if (entity.model && !prev[assetId(entityType, entity, AssetType.MODEL, entity.model.id)])
    mergeAsset(prev, entityType, AssetType.MODEL, entity, entity.model.id, sceneId);
  if (entity.video && !prev[assetId(entityType, entity, AssetType.VIDEO, entity.video.id)])
    mergeAsset(prev, entityType, AssetType.VIDEO, entity, entity.video.id, sceneId);
}

// eslint-disable-next-line require-yield
export function getUrl(assetType: AssetType, entity) {
  switch (assetType) {
    case AssetType.IMAGE:
      const image: IImageDAO = entity.image;

      if (!image) return null;

      const dimensions = Math.max(image.width, image.height);

      const imageUrls = getImageUrlCandidates(
        image.files,
        dimensions > 2000 ? Resolutions.FULL_2048 : Resolutions.MAX_4096,
      );

      return [imageUrls[0] || null, `/${image.name}`, imageUrls];
    case AssetType.VIDEO:
      const video: IVideoDAO = entity.video;
      const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;

      let preferredType;
      const defaultType = (preferredType = isSafari ? VideoType.HLS_264 : VideoType.DASH_264);

      if (videoEl.canPlayType('video/mp4; codecs=hvc1') !== '')
        preferredType = isSafari ? VideoType.HLS_265 : VideoType.HLS_265;
      else if (videoEl.canPlayType('video/webm; codecs=vp9') !== '')
        preferredType = isSafari ? preferredType : VideoType.DASH_WEBM;

      const preferred: Partial<IVideoFile> = getVideoFile(video.files, preferredType, defaultType);
      const fallback: Partial<IVideoFile> = getVideoFile(video.files, defaultType, null);
      return [preferred ? `/${preferred.filename}` : null, fallback ? `/${fallback.filename}` : null];
    case AssetType.MODEL:
      const model: IModelDAO = entity.model;

      if (model) return [`/${model.name}`, null];
  }
}

export function getVideoFile(files: IVideoFile[], preferredType: VideoType, defaultType?: VideoType) {
  return files && files.length
    ? files.reduce((prev, next) => {
        if (next.type === preferredType) return next;
        if (defaultType && next.type === defaultType && !prev) return next;
        return prev;
      }, null)
    : null;
}

export function getPreferredResolution(preferredResolution?: Resolutions) {
  if (preferredResolution) return preferredResolution;

  if (window.innerWidth > 2048) return Resolutions.MAX_4096;
  if (window.innerWidth > 1024) return Resolutions.FULL_2048;
  if (window.innerWidth > 512) return Resolutions.FULL_2048;
  if (window.innerWidth > 256) return Resolutions.FULL_2048;
  return Resolutions.MED_1024;
}

// Files ordered best-first: the preferred resolution, then progressively smaller ones.
// Larger derivatives are kept at the tail as a last resort so we still render something
// when only oversized files exist. Callers can walk this list when a file 403s/404s
// because its derivative was never written to S3.
export function getImageFileCandidates(files: IImageFile[], preferredResolution?: Resolutions): IImageFile[] {
  if (!files || !files.length) return [];

  const preferred = getPreferredResolution(preferredResolution);
  const webp = files.filter(f => f.type === ImageFileType.WEBP);
  const pool = webp.length ? webp : files;

  return [
    ...pool.filter(f => f.resolution <= preferred).sort((a, b) => b.resolution - a.resolution),
    ...pool.filter(f => f.resolution > preferred).sort((a, b) => a.resolution - b.resolution),
  ];
}

export function getPreferedImageFile(files: IImageFile[], preferredResolution?: Resolutions) {
  return getImageFileCandidates(files, preferredResolution)[0] || null;
}

// Derivatives sit alongside the upload: `<uuid>/img-max.webp`, `<uuid>/source.webp`.
// A webp upload sometimes skips derivative generation altogether, leaving the source as
// the only object actually present in S3 — so it is always the final rung of the ladder.
export function getSourceUrl(files: IImageFile[]) {
  const filename = files?.find(f => f?.filename?.includes('/'))?.filename;
  if (!filename) return null;

  return `/${filename.slice(0, filename.lastIndexOf('/'))}/source.webp`;
}

// The full ordered list of URLs to try for an image, best-first, ending at the source.
export function getImageUrlCandidates(files: IImageFile[], preferredResolution?: Resolutions): string[] {
  const urls = getImageFileCandidates(files, preferredResolution).map(f => `/${f.filename}`);
  const source = getSourceUrl(files);

  return source && !urls.includes(source) ? [...urls, source] : urls;
}

// eslint-disable-next-line require-yield
export function getDimensions(assetType: AssetType, entity) {
  switch (assetType) {
    case AssetType.IMAGE:
      const image: IImageDAO = entity.image;
      const file = getPreferedImageFile(image.files);

      if (!file) return null;

      const aspect = image.height / image.width;

      switch (file.resolution) {
        case Resolutions.MAX_4096:
          return { width: 4096, height: 4096 * aspect };
        case Resolutions.FULL_2048:
          return { width: 2048, height: 2048 * aspect };
        case Resolutions.MED_1024:
          return { width: 1024, height: 1024 * aspect };
        case Resolutions.SMALL_512:
          return { width: 512, height: 512 * aspect };
        case Resolutions.XSMALL_256:
          return { width: 256, height: 256 * aspect };
      }
      return null;
    case AssetType.VIDEO:
      const video: IVideoDAO = entity.video; //yield select(state => selectVideo(state, entity.video.id));
      return { width: video.width, height: video.height };
    case AssetType.MODEL:
      return null;
  }
}

// eslint-disable-next-line require-yield
function getOrientation(assetType: AssetType, entity) {
  switch (assetType) {
    case AssetType.IMAGE:
      const image: IImageDAO = entity.image;
      return image.orientation || 0;
    case AssetType.VIDEO:
      const video: IVideoDAO = entity.video;
      return video.orientation || 0;
    case AssetType.MODEL:
      return null;
  }
}
function getRotation(assetType: AssetType, entity) {
  switch (assetType) {
    case AssetType.IMAGE:
      const image: IImageDAO = entity.image;
      return image.rotation || 0;
    case AssetType.VIDEO:
      const video: IVideoDAO = entity.video;
      return video.rotation || 0;
    case AssetType.MODEL:
      return null;
  }
}

// Keep the start time clear of the end of the stream. Encodes often leave the audio
// timeline a fraction short of the video, and starting inside that trailing hole makes
// dash's GapController jump to the end while its seek fix drags playback back — a loop
// that never plays. A negative start is equally undefined, and `|| 0` doesn't catch it.
const START_END_MARGIN = 0.5;

function getStart(assetType: AssetType, entity) {
  switch (assetType) {
    case AssetType.VIDEO:
      const start = entity.sceneConfig?.start || 0;
      const duration = entity.video?.duration;

      if (!start || start < 0) return 0;

      const safe = duration ? Math.min(start, Math.max(0, duration - START_END_MARGIN)) : start;

      if (safe !== start) assetWarn('start:clamped', `${start} -> ${safe} (duration ${duration})`);

      return safe;
  }
  return null;
}

function mergeAsset(state, entityType, assetType, entity, mediaId, sceneId) {
  const [url, fallback, urls] = getUrl(assetType, entity);

  assetLog('parse', assetId(entityType, entity.id, assetType, mediaId), {
    url,
    fallback,
    candidates: urls,
    files: entity.image?.files?.map(f => ({ res: f.resolution, type: f.type, filename: f.filename })),
  });
  const dimensions = getDimensions(assetType, entity);
  const orientation = getOrientation(assetType, entity);
  const rotation = getRotation(assetType, entity);
  const start = getStart(assetType, entity);

  if (!url) assetWarn('parse', 'no url resolved, asset will not be created', { entityType, assetType, mediaId });

  if (url)
    state[assetId(entityType, entity.id, assetType, mediaId)] = {
      id: assetId(entityType, entity.id, assetType, mediaId),
      url,
      fallback,
      urls,
      type: assetType,
      loading: LoadingState.DEFAULT,
      progress: 0,
      dimensions,
      orientation,
      rotation,
      start,
      sceneId,
    };
}

function* watchGetFulfilled() {
  yield takeLatest(getRequest.fulfilled, getRequestFulfilled);
}

function* watchModuleRequest() {
  yield takeLatest(moduleRequest.fulfilled, moduleRequestFulfilled);
}

function* watchRestore() {
  yield takeLatest(restoreState, restoreStateSaga);
}

function* watchCreateFulfilled() {
  yield takeLatest(createRequest.fulfilled, createRequestFulfilled);
}

function* watchUpdateState() {
  yield takeLatest(updateState.type, updateStateSaga);
}

function* watchPreview() {
  yield takeLatest(preview, previewSaga);
}

function* watchQueue() {
  yield takeLatest(queueItem.type, queueSaga);
}

export function* parseSaga() {
  yield all([
    fork(watchUpdateState),
    fork(watchRestore),
    fork(watchModuleRequest),
    fork(watchGetFulfilled),
    fork(watchCreateFulfilled),
    fork(watchPreview),
    fork(watchQueue),
  ]);
}
